import { AxiosError, AxiosResponse } from "axios";
import { downloadEmploymentsExport } from "src/Pages/Company/OrganizationStructure/api";
import { pollWithBackoff, PollTimeoutError } from "src/utils/pollWithBackoff";

/**
 * The employments-export report is generated asynchronously on the backend.
 * While the file is still being built, the GET endpoint responds with HTTP 404
 * and a body containing the error code `FILE_REPORT_NOT_FOUND`. Fast formats
 * (CSV) are usually ready immediately, but slower formats (XLSX) may take a few
 * seconds. This helper retries the same GET with a fixed backoff, treating that
 * specific 404 as "still pending", until the report is ready (200) or a timeout
 * is reached. Delays/limits are shared with other report-polling endpoints,
 * see `src/utils/pollWithBackoff.ts`.
 */

export const PENDING_REPORT_ERROR_CODE = "FILE_REPORT_NOT_FOUND";

export class ReportTimeoutError extends Error {
  constructor() {
    super("Employments export report was not ready in time");
    this.name = "ReportTimeoutError";
  }
}

interface DownloadEmploymentsExportParams {
  companyId: string;
  structureId: string;
  contentType: string;
  from: string;
  to: string;
  filter: string;
}

interface RetryOptions {
  download?: typeof downloadEmploymentsExport;
  wait?: (delayMs: number, signal?: AbortSignal) => Promise<void>;
  now?: () => number;
}

/**
 * Reads the error code out of a 404 response. Because the request uses
 * `responseType: "blob"`, the error body arrives as a Blob and must be parsed
 * as JSON. The backend returns `{ errors: [{ code, ... }] }`.
 */
const parseErrorBody = async (data: unknown): Promise<unknown> => {
  // Blob (or Blob-like) body: responseType "blob" delivers the error payload
  // as a Blob, so read it as text before parsing.
  if (data && typeof (data as { text?: unknown }).text === "function") {
    return JSON.parse(await (data as Blob).text());
  }

  if (typeof data === "string") {
    return JSON.parse(data);
  }

  return data;
};

const extractErrorCode = async (data: unknown): Promise<string | undefined> => {
  try {
    const parsed = await parseErrorBody(data);

    const body = parsed as {
      errors?: Array<{ code?: string }>;
      code?: string;
    };

    return body?.errors?.[0]?.code ?? body?.code;
  } catch {
    return undefined;
  }
};

const isPendingReportError = async (error: unknown): Promise<boolean> => {
  const axiosError = error as AxiosError;
  if (axiosError?.response?.status !== 404) return false;

  const code = await extractErrorCode(axiosError.response.data);
  return code === PENDING_REPORT_ERROR_CODE;
};

/**
 * Performs the employments-export GET, retrying while the backend reports the
 * report as still pending (404 FILE_REPORT_NOT_FOUND). Any other error is
 * rethrown immediately. Throws {@link ReportTimeoutError} if the report is not
 * ready before the timeout.
 */
export const downloadEmploymentsExportWithRetry = async (
  params: DownloadEmploymentsExportParams,
  { download = downloadEmploymentsExport, wait, now }: RetryOptions = {}
): Promise<AxiosResponse> => {
  try {
    return await pollWithBackoff<AxiosResponse>({
      wait,
      now,
      attempt: async () => {
        try {
          return { done: true, value: await download(params) };
        } catch (error) {
          if (await isPendingReportError(error)) return { done: false };
          throw error;
        }
      },
    });
  } catch (error) {
    if (error instanceof PollTimeoutError) throw new ReportTimeoutError();
    throw error;
  }
};
